import { useInfiniteQuery } from '@tanstack/react-query'
import React, { useEffect, useMemo } from 'react'
import { Helmet } from 'react-helmet'
import type { AskReportingChartQuestion } from 'src/agent/reporting/chartQuestion.types'
import * as reportingStyle from 'src/app/Reporting/Reporting.style'
import LayoutPage from 'src/component-layout/LayoutPage/LayoutPage'
import { useTranslate } from 'src/localization/Trns'
import { liveReportingQueries } from 'src/queries/liveReporting/liveReporting.queries'
import { reportingErrorStatus } from 'src/queries/reporting/reporting.errors'
import { useQueryOwnUser } from 'src/queries/users.queries'

import PinnedReportingSection from './PinnedReportingSection/PinnedReportingSection'
import { useReportingAccess } from './reportingAccess'

export interface ReportingPinnedPageProps {
  onCreateReport: () => void
  onAskQuestion?: AskReportingChartQuestion
  onAccessDenied?: () => void
  embedded?: boolean
}

const ReportingPinnedPage: React.FC<ReportingPinnedPageProps> = ({
  embedded = false,
  onCreateReport,
  onAskQuestion,
  onAccessDenied,
}) => {
  const translate = useTranslate()
  const reportingEnabled = useReportingAccess()
  const { data: currentUser } = useQueryOwnUser({})
  const liveReportsQuery = useInfiniteQuery({
    ...liveReportingQueries.summaries({
      actorId: currentUser?.id,
      organizationId: currentUser?.organizationId,
      reportingEnabled,
    }),
    refetchOnMount: true,
  })
  const summaryStatus = reportingErrorStatus(liveReportsQuery.error)
  const summaryAuthorizationLost =
    summaryStatus === 401 || summaryStatus === 403
  const reports = useMemo(
    () => liveReportsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [liveReportsQuery.data?.pages],
  )
  useEffect(() => {
    if (summaryAuthorizationLost) onAccessDenied?.()
  }, [onAccessDenied, summaryAuthorizationLost])
  const pinnedContent = (
    <div css={reportingStyle.page}>
      <PinnedReportingSection
        actorId={currentUser?.id}
        organizationId={currentUser?.organizationId}
        reportingEnabled={reportingEnabled && !summaryAuthorizationLost}
        reports={summaryAuthorizationLost ? [] : reports}
        loading={liveReportsQuery.isLoading}
        loadingMore={liveReportsQuery.isFetchingNextPage}
        hasMore={liveReportsQuery.hasNextPage}
        onLoadMore={() => {
          void liveReportsQuery.fetchNextPage()
        }}
        error={liveReportsQuery.isError}
        onRetryList={() => {
          void liveReportsQuery.refetch()
        }}
        onAskQuestion={onAskQuestion}
        onAccessDenied={onAccessDenied}
      />
    </div>
  )

  return (
    <>
      <Helmet>
        <title>{translate('Reporting')}</title>
      </Helmet>
      {embedded ? (
        pinnedContent
      ) : (
        <LayoutPage
          title={<span>{translate('Reporting')}</span>}
          subtitle={
            <span css={reportingStyle.subtitle}>
              {translate(
                'Review company spend across accounts, teams, merchants, and people.',
              )}
            </span>
          }
          actions={
            <button type="button" onClick={onCreateReport}>
              {translate('Create report')}
            </button>
          }
        >
          {pinnedContent}
        </LayoutPage>
      )}
    </>
  )
}

export default ReportingPinnedPage
